<script>
import {useRoute, useRouter} from "vue-router";
import {useUserStore} from "../../../stores/user";
import {ApplicationHelper} from "../../../helpers/ApplicationHelper";
import DonationCard from "../components/DonationCard.vue";
import {useSubscriptionStore} from "../../../stores/subscription";
import SubscriptionCard from "../components/SubscriptionCard.vue";
import Drawer from "../../components/Drawer.vue";
import LoadingSpinner from "../../components/loadingSpinner.vue";
import Icon from "../../components/Icon.vue";
import BaseModal from "../../components/BaseModal.vue";
import Popup from "../../components/popup.vue";
import Tooltip from "../../components/tooltip.vue";
import {useLandingPageStore} from "@/javascript/stores/landingPage";
import DataCard from "../../components/overview/DataCard.vue";


export default {
  name: "SubscriptionManagement",
  components: {Tooltip, Popup, BaseModal, Icon, LoadingSpinner, Drawer, SubscriptionCard, DonationCard, DataCard},
  setup() {

    const router = useRouter();
    const route = useRoute();

    const userStore = useUserStore();
    const subscriptionStore = useSubscriptionStore();
    const landingPageStore = useLandingPageStore();

    return {
      route,
      router,
      userStore,
      subscriptionStore,
      landingPageStore,
    };
  },
  data() {
    return {
      activeSubscription: {},
      showCancelModal: false,
      showPauseModal: false,
      showResumeModal: false,
      modalProcessing: false
    };
  },
  computed: {
    ApplicationHelper() {
      return ApplicationHelper;
    },
    subscriptions() {
      return this.userStore.subscriptions;
    },
    subscriptionCanBeCancelled() {
      return this.activeSubscription.status === 'active' || this.activeSubscription.status === 'trialing';
    },
    subscriptionCanBePaused() {
      return this.activeSubscription.status === 'active';
    },
    subscriptionCanBeResumed() {
      return this.activeSubscription.status === 'paused';
    },
    loaded() {
      return !!this.userStore.user;
    }
  },
  mounted() {
    // Get the donation ID from the route
    const id = this.route.params.id;
    if (id) {
      const subscription = this.subscriptions.find(s => s.id?.toString() === id || s.uuid?.toString() === id);
      // If ID present and matches a donation, toggle the show view for the donation. Allows direct lining to the drawer
      if (subscription) {
        this.openDrawer(subscription);
      } else {
        // Otherwise remove the invalid ID from the route
        this.closeDrawer();
      }
    }
  },

  methods: {
    openDrawer(subscription) {
      this.router.replace({
        params: {
          ...this.route.params,
          id: subscription.uuid
        }
      });
      this.$refs.drawer.show();
      this.subscriptionStore.setOrganizationId(subscription.organization_id);
      this.subscriptionStore.getSubscription(subscription.uuid).then((res) => {
        this.activeSubscription = res;
      });

    },

    closeDrawer() {
      this.activeSubscription = {};
      this.router.replace({
        params: {
          ...this.route.params,
          id: ''
        }
      });
    },

    cancelButtonClick() {
      this.showCancelModal = true;
    },

    pauseButtonClick() {
      this.showPauseModal = true;
    },

    resumeButtonClick() {
      this.showResumeModal = true;
    },

    // Pauses an active subscription
    pauseSubscription() {
      this.modalProcessing = true;
      this.subscriptionStore.pauseSubscription(this.activeSubscription).then((res) => {
        this.onSubscriptionSave(res?.json?.record);
      });
    },

    // Stops an active subscription
    cancelSubscription() {
      this.modalProcessing = true;
      this.subscriptionStore.cancelSubscription(this.activeSubscription).then((res) => {
        this.onSubscriptionSave(res?.json?.record);
      });
    },

    // Resumes a paused subscription
    resumeSubscription() {
      this.modalProcessing = true;
      this.subscriptionStore.resumeSubscription(this.activeSubscription).then((res) => {
        this.onSubscriptionSave(res?.json?.record);
      });
    },

    // After subscription action in modal. Update the current record and trigger reload of all records
    onSubscriptionSave(record) {
      this.modalProcessing = false;
      this.closeModals();
      this.userStore.getUserSubscriptions();
      if (record) {
        this.activeSubscription = record;
        this.$refs.popup.show();
      }
    },

    closeModals() {
      this.showPauseModal = false;
      this.showCancelModal = false;
      this.showResumeModal = false;
    }

  }
};
</script>

<template>
  <data-card
    :loaded="loaded"
    title="Recurring Gifts"
    title-class="text-lg font-semibold"
    additional-text="Your recurring gifts to supported funds."
    additional-text-class="text-xs text-gray-500 font-normal"
    :left-sidebar="false"
    bordered
    border-class="border border-slate-200 rounded-lg shadow"
  >
    <div class="card-scroll">
      <ul class="flex flex-col gap-2">
        <li
          v-for="(subscription, idx) in subscriptions"
          :key="subscription.id"
          class="w-full"
        >
          <subscription-card
            :subscription="subscription"
            :alt-row="idx % 2 === 1"
            clickable
            @click="openDrawer(subscription)"
          />
        </li>
      </ul>
    </div>
  </data-card>

  <drawer
    ref="drawer"
    size="large"
    @hide="closeDrawer"
  >
    <div
      v-if="subscriptionStore.loadingRecord"
      class="flex justify-center items-center border h-full"
    >
      <loading-spinner
        size="small"
      />
    </div>
    <div
      v-else
      class="subscription-details-drawer"
    >
      <!--      <div>  invoice#  downloadInv  subLink? useUrl</div>-->

      <div class="subscription-amount-header">
        <div class="flex flex-col items-center">
          <div class="text-xs font-bold uppercase">
            <span>Your {{ subscriptionStore.intervalString(activeSubscription) }} </span>
            <span> gift to {{ activeSubscription.organization_name }}</span>
          </div>
          <div class="m-2">
            {{ ApplicationHelper.formatMoney(activeSubscription.amount) }}
          </div>
          <div class="text-sm">
            {{ activeSubscription.organization_currency || "USD" }}
          </div>
        </div>
      </div>

      <div class="details-table">
        <div class="font-semibold mt-4 mb-2">
          Recurring Gift Details
        </div>
        <table class="border-spacing-2">
          <tr>
            <td>Amount</td>
            <td>{{ ApplicationHelper.formatMoney(activeSubscription.amount) }}</td>
          </tr>
          <tr v-if="activeSubscription.target_name">
            <td>Designation</td>
            <td> {{ activeSubscription.target_name }}</td>
          </tr>
          <tr class="divider" />

          <tr>
            <td>Payment Method</td>
            <td>
              <img
                v-if="activeSubscription.processor_type?.includes('Stripe')"
                src="@/assets/logos/stripe.svg"
                style="height: 24px;"
                alt="Stripe Logo"
              >
              <img
                v-else-if="activeSubscription.processor_type?.includes('Helcim')"
                src="@/assets/logos/helcim_logo.svg"
                style="height: 24px;"
                alt="Helcim Logo"
              >
            </td>
          </tr>

          <tr>
            <td>Status</td>
            <td>
              <div
                class="subscription-status-pill pill"
                :class="{
                  [`status--${activeSubscription.status || 'pending'}`]: true,
                }"
              >
                {{ subscriptionStore.statusString(activeSubscription) }}
              </div>
            </td>
          </tr>

          <tr v-if="activeSubscription.fee_amount > 0">
            <td>Platform Contribution</td>
            <td>{{ ApplicationHelper.formatMoney(activeSubscription.fee_amount, true) }}</td>
          </tr>

          <tr>
            <td>
              Giving Frequency
            </td>

            <td>
              {{ subscriptionStore.intervalString(activeSubscription) }}
            </td>
          </tr>

          <!-- Only show when the subscription started for active / begun subscriptions -->
          <tr v-if="activeSubscription.status !== 'trialing'">
            <td>
              Started
            </td>
            <td>
              <div v-if="activeSubscription.delay_start">
                <!-- Subscriptions started when created unless delayed when set up -->
                {{ ApplicationHelper.formatDate(activeSubscription.delay_start_date, {weekday: false, time: false}) }}
              </div>
              <div v-else>
                {{ ApplicationHelper.formatDate(activeSubscription.created_at, {weekday: false, time: false}) }}
              </div>
            </td>
          </tr>

          <tr v-if="activeSubscription.status !== 'cancelled'">
            <td v-if="activeSubscription.status === 'trialing'">
              Starts
            </td>
            <td
              v-else
              class="flex flex-row"
            >
              <div>
                Next Giving Date
              </div>
              <tooltip
                v-if="activeSubscription.status === 'paused'"
                message="Your next gift will not be processed unless you resume your recurring gift."
                class="ml-2"
              />
            </td>
            <td>
              <div>
                {{ ApplicationHelper.formatDate(activeSubscription.next_payment_date, {weekday: false, time: false}) }}
              </div>
            </td>
          </tr>

          <!-- Delayed subscriptions will never have any successful payments -->
          <tr v-if="activeSubscription.status !== 'trialing'">
            <td>
              Number of Gifts
            </td>
            <td>
              {{ activeSubscription.successful_payments_count }}
            </td>
          </tr>


          <tr>
            <td>Invoice Number</td>
            <td class="uppercase">
              {{ activeSubscription.external_subscription_identifier }}
            </td>
          </tr>
        </table>

        <div
          v-if="subscriptionCanBePaused"
          class="branded-action-button mt-4"
          @click="pauseButtonClick"
        >
          <div class="content">
            <div
              class="flex flex-row content-center items-center"
            >
              <icon
                icon="pause"
                size="24px"
                color="#4F4F4F"
              />
              <div class="ml-2 body-1-medium">
                Pause Recurring Gift
              </div>
            </div>
            <icon icon="chevron-right" />
          </div>
        </div>

        <div
          v-if="subscriptionCanBeCancelled"
          class="branded-action-button mt-4"
          @click="cancelButtonClick"
        >
          <div class="content">
            <div
              class="flex flex-row content-center items-center"
            >
              <icon
                icon="ban"
                size="24px"
                color="#4F4F4F"
              />
              <div class="ml-2 body-1-medium">
                Stop Recurring Gift
              </div>
            </div>
            <icon icon="chevron-right" />
          </div>
        </div>

        <div
          v-if="subscriptionCanBeResumed"
          class="branded-action-button my-4"
          @click="resumeButtonClick"
        >
          <div class="content">
            <div class="flex flex-row content-center items-center">
              <div class="ml-2 body-1-medium">
                Resume Recurring Gift
              </div>
            </div>
            <icon icon="chevron-right" />
          </div>
        </div>
      </div>
    </div>
  </drawer>

  <base-modal
    v-if="showCancelModal"
    ref="cancelSubscriptionModal"
    size="small"
    @close="showCancelModal = false;"
  >
    <template #header-slot>
      <div
        v-if="!modalProcessing"
        class="subtitle-regular"
      >
        Stop Recurring Gift
      </div>
    </template>

    <div v-if="modalProcessing">
      <loading-spinner
        size="small"
        class="m-12"
      />
    </div>

    <div v-else>
      <div class="mt-2">
        Are you sure you want to stop your recurring gift to {{ activeSubscription.organization_name }}?
      </div>
      <div>
        This stops all future gifts.
      </div>

      <div
        v-if="subscriptionCanBePaused"
        class="mt-2"
      >
        You can pause your recurring gift instead if you would like to resume it in the future.
      </div>

      <div class="bg-red-100 border-red-700 p-4 mt-2 mb-4 flex flex-row gap-3 rounded-md">
        <icon icon="warning-outline" />
        <div>Stopping a recurring gift cannot be undone.</div>
      </div>
      <div class="my-4 caption-bold">
        * Canceling or pausing your recurring gift will not impact any past contributions.
        If a gift has already been processed for the current billing period, the cancellation / pause will take effect
        starting with your next scheduled gift.
      </div>
      <div class="flex flex-row gap-2 mt-2">
        <button
          class="btn btn-filled"
          @click="closeModals"
        >
          Go Back
        </button>
        <button
          v-if="subscriptionCanBePaused"
          class="btn btn-filled"
          @click="pauseSubscription"
        >
          Pause Instead
        </button>
        <div class="flex-1" />
        <button
          class="btn btn-outline btn-warning border-0"
          @click="cancelSubscription"
        >
          Stop Recurring Gift
        </button>
      </div>
    </div>
  </base-modal>

  <base-modal
    v-if="showPauseModal"
    ref="pauseSubscriptionModal"
    @close="showPauseModal = false;"
  >
    <template #header-slot>
      <div class="subtitle-regular">
        Pause Recurring Gift
      </div>
    </template>

    <div v-if="modalProcessing">
      <loading-spinner
        size="small"
        class="m-12"
      />
    </div>

    <div v-else>
      <div class="mt-2">
        Are you sure you want to pause your recurring gift to {{ activeSubscription.organization_name }}?
      </div>
      <div class="mt-2">
        This will pause all future gifts until you resume your giving. You can resume your giving at any time.
      </div>

      <div class="my-4 caption-bold">
        * Pausing your recurring gift will not impact any past contributions.
        If a gift has already been processed for the current billing period, the pause will take effect starting with
        your next scheduled gift.
      </div>

      <div class="flex flex-row gap-2 mt-2">
        <button
          class="btn btn-filled"
          @click="closeModals"
        >
          Go Back
        </button>
        <div class="flex-1" />
        <button
          class="btn btn-outline btn-warning border-0"
          @click="pauseSubscription"
        >
          Pause Recurring Gift
        </button>
      </div>
    </div>
  </base-modal>

  <base-modal
    v-if="showResumeModal"
    ref="resumeSubscriptionModal"
    size="small"
    @close="showResumeModal = false;"
  >
    <template #header-slot>
      <div>Resume Recurring Gift</div>
      <div v-if="modalProcessing">
        <loading-spinner
          size="small"
          class="m-12"
        />
      </div>
      <div v-else>
        <div class="my-4">
          This will resume your giving to {{ activeSubscription.organization_name }}.
        </div>
        <div class="my-4 caption-bold">
          * Resuming your recurring gift will restart your contributions according to your original giving schedule and
          selected frequency.
        </div>
        <div class="flex flex-row gap-2 justify-between">
          <button
            class="btn btn-filled"
            @click="resumeSubscription"
          >
            Resume Giving
          </button>
          <div
            class="btn btn-outline"
            @click="closeModals"
          >
            Go Back
          </div>
        </div>
      </div>
    </template>
  </base-modal>

  <popup
    ref="popup"
    message="Recurring Gift Updated!"
  />
</template>

<style scoped lang="scss">
.branded-action-button {
  @apply rounded-md cursor-pointer;
  background-color: rgba(16, 190, 90, 0.1);
  position: relative;

  .content {
    @apply w-full flex flex-row justify-between gap-3 rounded-md items-center;
    @apply relative z-10;

  }

  &::after {
    content: "";
    position: absolute;
    @apply absolute top-0 left-0 right-0 bottom-0 opacity-0;
    @apply rounded-md cursor-pointer;
    background: #08602D;
    background: linear-gradient(123deg, rgba(16, 190, 90, 0.2) 52%, rgba(0, 189, 255, 0.17) 100%);
    transition: opacity 0.5s ease-in-out;
    z-index: 1;
    pointer-events: none; /* Allows clicks to pass through */
  }

  &:hover::after {
    opacity: 1;
  }
}

.subscription-details-drawer {
  @apply mt-16 m-6;
  overflow-y: auto;
  max-height: calc(100vh - 100px);

  .subscription-amount-header {
    @apply p-12 rounded-md;
    @apply flex flex-col items-center justify-center text-center;
    @apply text-3xl font-semibold;
    background: linear-gradient(123deg, rgba(16, 190, 90, 0.2) 52%, rgba(0, 189, 255, 0.17) 100%);
  }

  .details-table {
    @apply w-full overflow-x-auto;
    table {
      @apply w-full;
      tr {
        @apply flex flex-col sm:flex-row justify-between mt-2 gap-2;

        td {
          @apply break-words;
        }

        td:nth-child(1) {
          // Labels
          @apply font-semibold w-full sm:w-auto;
        }

        td:nth-child(2) {
          @apply sm:flex-1;
          // Removed w-full so pills don't stretch on mobile
        }

        &.divider {
          @apply m-4 border border-slate-200;
        }
      }
    }
  }

  .subscription-status-pill {
    @apply inline-flex items-center justify-center;
    @apply text-xs font-bold text-white rounded-full py-1 px-2 whitespace-nowrap;
    @apply bg-default_secondary;
  }
}

.card-scroll {
  max-height: 220px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}

.card-scroll::-webkit-scrollbar:horizontal {
  display: none;
}

.card-scroll :deep(.donation-card:first-child) {
  margin-top: 0 !important;
}
</style>
